// Scrubbing for anything that leaves the browser (crash messages/stacks, event meta).
// Pure module, no imports: unit-tested with `node --test` (test/unit/).

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Indonesian mobile/landline numbers in the usual spellings: +62 812-3456-7890, 0812 3456 7890, 6281234567890.
const PHONE = /(?:\+?62|\b0)[\s-]?\d{2,4}(?:[\s-]?\d{3,4}){1,3}\b/g;
// key=value / key: value pairs whose key names a secret.
const SECRET_PAIR = /\b(token|api[_-]?key|apikey|password|passwd|pwd|secret|authorization|auth|session|sesi)\b(["']?\s*[:=]\s*["']?)([^\s"'&,;)}]+)/gi;
const BEARER = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
// JWT-looking strings and long opaque tokens (hex / base64-ish, >= 24 chars with a digit).
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const LONG_TOKEN = /\b(?=[A-Za-z0-9_-]*\d)(?=[A-Za-z0-9_-]*[A-Za-z])[A-Za-z0-9_-]{24,}\b/g;
// Query strings on URLs: may carry search terms or tokens.
const URL_QUERY = /(https?:\/\/[^\s?#)"']+)[?#][^\s)"']*/g;
// Long digit runs (ids, amounts, NIK, card/account numbers). In stacks a ":<line>:<col>" must survive.
const DIGITS_MSG = /\d{6,}/g;
const DIGITS_STACK = /(?<![:\d])\d{6,}/g;

function base(s) {
  return s
    .replace(URL_QUERY, '$1')
    .replace(JWT, '[token]')
    .replace(BEARER, 'Bearer [token]')
    .replace(SECRET_PAIR, '$1$2[token]')
    .replace(EMAIL, '[email]');
}

export function scrubMessage(input, max = 2000) {
  try {
    if (input == null) return '';
    let s = base(String(input));
    s = s.replace(PHONE, '[phone]').replace(LONG_TOKEN, '[token]').replace(DIGITS_MSG, '[num]');
    return s.length > max ? s.slice(0, max) : s;
  } catch {
    return '';
  }
}

// Stacks keep file:line:col; only the message-like parts are rewritten.
export function scrubStack(input, max = 16000) {
  try {
    if (input == null) return '';
    let s = base(String(input));
    s = s.replace(LONG_TOKEN, '[token]').replace(DIGITS_STACK, '[num]');
    return s.length > max ? s.slice(0, max) : s;
  } catch {
    return '';
  }
}

// Firefox/Safari stacks are "fn@url:1:2"; the platform fingerprints on lines starting with "at ".
export function normalizeStack(stack, fallbackFrame) {
  const lines = String(stack || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const out = [];
  for (const l of lines) {
    if (l.startsWith('at ')) out.push(l);
    else if (/@.+:\d+(:\d+)?$/.test(l)) {
      const i = l.lastIndexOf('@');
      const fn = l.slice(0, i) || '<anonymous>';
      out.push(`at ${fn} (${l.slice(i + 1)})`);
    } else out.push(l);
  }
  if (!out.some((l) => l.startsWith('at ')) && fallbackFrame) out.push(`at ${fallbackFrame}`);
  return out.join('\n');
}

export function fingerprint(report) {
  const frames = String(report.stack || '').split('\n').filter((l) => l.trim().startsWith('at ')).slice(0, 5);
  return [report.exception_class || '', report.message || '', ...frames.map((l) => l.trim())].join('|');
}

// A route/endpoint template: no query, numeric/uuid-ish segments become ":id". Never the query.
export function pathTemplate(url) {
  try {
    let p = String(url || '');
    p = p.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0];
    p = p.replace(/^\/?api\/(admin\/)?/, '').replace(/^\/+/, '');
    return p
      .split('/')
      .map((seg) => (/^\d+$/.test(seg) || /^[0-9a-f-]{16,}$/i.test(seg) ? ':id' : seg))
      .join('/')
      .slice(0, 120);
  } catch {
    return '';
  }
}

// Accutrax tenant slug: first label of the API host minus "-api" (super-electric-api.accutrax.id -> super-electric).
export function tenantFromHost(host) {
  const h = String(host || '').toLowerCase().replace(/:\d+$/, '');
  if (!h || h === 'localhost' || /^[\d.]+$/.test(h) || !h.includes('.')) return null;
  const first = h.split('.')[0];
  return first.replace(/-api$/, '') || null;
}
